import { useMemo } from "react";
import type { ChangePayload, EnrichedOrderItem } from "@/features/order/types";
import { useChangeRequestItems } from "@/features/order/hooks";
import { useQuotationItems } from "@/features/quotation/hooks";

const UNIT_LABELS: Record<string, string> = {
  cx_kg: "Cx/Kg",
  cx_un: "Cx/Un",
  bdj: "Bandeja",
  un: "Un",
  sc_kg: "Sc/Kg",
};

const FIELD_LABELS = {
  quantity: "Quantidade",
  price: "Preço",
} as const;

interface ChangeRequestDiffProps {
  payload: ChangePayload;
  orderItems: EnrichedOrderItem[];
  quotationId?: string;
  storeId?: string;
  changeRequestId?: string;
}

function getUnitLabel(unitType: string) {
  return UNIT_LABELS[unitType] ?? unitType;
}

function formatBRL(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(value);
}

function ChangeLine({
  field,
  current,
  requested,
}: {
  field: "quantity" | "price";
  current: string;
  requested: string;
}) {
  return (
    <span className="change-request-diff-change-line">
      <span className="change-request-visually-hidden">
        {FIELD_LABELS[field]}
      </span>
      <span className="change-request-diff-comparison-block">
        <span className="change-request-diff-comparison-label">Atual</span>
        <span className="change-request-diff-comparison-value">{current}</span>
      </span>
      <span className="change-request-diff-comparison-block">
        <span className="change-request-diff-comparison-label">Solicitado</span>
        <span className="change-request-diff-comparison-value">
          {requested}
        </span>
      </span>
    </span>
  );
}

export function ChangeRequestDiff({
  payload,
  orderItems,
  quotationId,
  storeId,
  changeRequestId,
}: ChangeRequestDiffProps) {
  const removes = payload.remove ?? [];
  const updates = payload.update ?? [];
  const adds = payload.add ?? [];

  const { data: qItemsData } = useQuotationItems(quotationId ?? "", storeId);
  const { data: historicalItems = [] } = useChangeRequestItems(changeRequestId);
  const qItemsById = useMemo(() => {
    return new Map(
      [...historicalItems, ...(qItemsData?.items ?? [])].map((item) => [
        item.id,
        item,
      ]),
    );
  }, [historicalItems, qItemsData]);

  const total = removes.length + updates.length + adds.length;
  if (total === 0) return null;

  const summaryParts: string[] = [];
  if (updates.length > 0) {
    summaryParts.push(
      `${updates.length} ${updates.length === 1 ? "alteração" : "alterações"}`,
    );
  }
  if (removes.length > 0) {
    summaryParts.push(
      `${removes.length} ${removes.length === 1 ? "remoção" : "remoções"}`,
    );
  }
  if (adds.length > 0) {
    summaryParts.push(
      `${adds.length} ${adds.length === 1 ? "adição" : "adições"}`,
    );
  }

  const itemsById = new Map(orderItems.map((item) => [item.id, item]));

  return (
    <div className="change-request-diff">
      <p className="change-request-diff-summary">{summaryParts.join(", ")}</p>

      <div className="change-request-diff-rows">
        {updates.map((update, index) => {
          const item = itemsById.get(update.order_item_id);
          if (!item) {
            return (
              <div
                className="change-request-diff-row"
                data-kind="update"
                data-unresolved="true"
                key={`${update.order_item_id}-${index}`}
              >
                <span className="change-request-diff-kind">Alterar</span>
                <span className="change-request-diff-product">
                  Item do pedido indisponível
                </span>
                <span className="change-request-diff-value">
                  {update.quantity !== undefined && (
                    <ChangeLine
                      field="quantity"
                      current="Indisponível"
                      requested={String(update.quantity)}
                    />
                  )}
                  {update.final_price !== undefined && (
                    <ChangeLine
                      field="price"
                      current="Indisponível"
                      requested={formatBRL(update.final_price)}
                    />
                  )}
                </span>
              </div>
            );
          }

          return (
            <div
              className="change-request-diff-row"
              data-kind="update"
              key={`${update.order_item_id}-${index}`}
            >
              <span className="change-request-diff-kind">Alterar</span>
              <span className="change-request-diff-product">
                {item.product_name}
              </span>
              <span className="change-request-diff-value">
                {update.quantity !== undefined && (
                  <ChangeLine
                    field="quantity"
                    current={`${item.quantity} ${getUnitLabel(item.unit_type)}`}
                    requested={`${update.quantity} ${getUnitLabel(item.unit_type)}`}
                  />
                )}
                {update.final_price !== undefined && (
                  <ChangeLine
                    field="price"
                    current={formatBRL(item.final_price)}
                    requested={formatBRL(update.final_price)}
                  />
                )}
              </span>
            </div>
          );
        })}

        {removes.map((itemId, index) => {
          const item = itemsById.get(itemId);
          if (!item) {
            return (
              <div
                className="change-request-diff-row"
                data-kind="remove"
                data-unresolved="true"
                key={`${itemId}-${index}`}
              >
                <span className="change-request-diff-kind">Remover</span>
                <span className="change-request-diff-product">
                  Item do pedido indisponível
                </span>
                <span className="change-request-diff-value">
                  <span className="change-request-diff-removal-note">
                    Item será removido
                  </span>
                </span>
              </div>
            );
          }

          return (
            <div
              className="change-request-diff-row"
              data-kind="remove"
              key={`${itemId}-${index}`}
            >
              <span className="change-request-diff-kind">Remover</span>
              <span className="change-request-diff-product">
                {item.product_name}
              </span>
              <span className="change-request-diff-value">
                <span className="change-request-diff-removal-note">
                  Item será removido
                </span>
                <span className="change-request-diff-removal-value">
                  {item.quantity} {getUnitLabel(item.unit_type)}
                </span>
              </span>
            </div>
          );
        })}

        {adds.map((add, index) => {
          const item = qItemsById.get(add.quotation_item_id);

          return (
            <div
              className="change-request-diff-row"
              data-kind="add"
              key={`${add.quotation_item_id}-${index}`}
            >
              <span className="change-request-diff-kind">Novo</span>
              <span className="change-request-diff-product">
                {item?.product_name ?? "Novo item"}
              </span>
              <span className="change-request-diff-value">
                <span>
                  {add.quantity} {getUnitLabel(item?.unit_type ?? "un")}
                </span>
                {add.final_price !== undefined && (
                  <span>{formatBRL(add.final_price)}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
